import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

export interface Segment {
  label: string | React.ReactNode
}

export interface SegmentedControlSegmentProps {
  label: string | React.ReactNode
  isSelected: boolean
  setSelected: () => void
  layoutId?: string
}

const SegmentedControlSegment: React.FC<SegmentedControlSegmentProps> = ({
  label,
  isSelected,
  setSelected,
  layoutId = 'segmentedControl',
}) => {
  const commonProps: React.ComponentPropsWithoutRef<typeof Box> = {
    alignItems: 'stretch',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 's4',
    style: {
      flex: '1',
    },
  } as const

  if (isSelected) {
    return (
      <Box {...commonProps} position="relative">
        <Box.Motion
          borderRadius={{ all: 'abs100' }}
          position="absolute"
          layout
          layoutId={layoutId}
          background="grey000White"
          boxShadow="s50"
          style={{
            zIndex: '0',
            top: '4px',
            left: '4px',
            bottom: '4px',
            right: '4px',
          }}
        />
        <Box style={{ zIndex: '1' }} paddingX="s24" paddingY="s4">
          {typeof label === 'string' ? (
            <Text size="200" weight="semibold" whiteSpace="nowrap">
              {label}
            </Text>
          ) : (
            label
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      textAlign="center"
      paddingX="s24"
      paddingY="s4"
      onClick={setSelected}
      {...commonProps}
      style={{ verticalAlign: 'middle' }}
    >
      <Box padding="s4" flexGrow>
        <Text size="200" weight="normal">
          {label}
        </Text>
      </Box>
    </Box>
  )
}

export interface SegmentedControlProps {
  segments: Array<Segment>
  onChangeSelected?: (index: number) => void
  initialSegment?: number
  layoutId?: string
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  segments,
  onChangeSelected,
  initialSegment = 0,
  layoutId,
}) => {
  if (initialSegment >= segments.length) {
    throw new Error('The initial segment is out of bounds')
  }

  const [currentSegment, setCurrentSegment] = useState<number>(initialSegment)

  if (segments.length < 2) {
    return null
  }

  return (
    <Box
      flexDirection="row"
      alignSelf="center"
      maxWidth="widePage"
      background="grey200"
      borderRadius={{ all: 'abs100' }}
      gap="s2"
    >
      {segments.map((segment, index) => (
        <SegmentedControlSegment
          key={index}
          label={segment.label}
          isSelected={index === currentSegment}
          setSelected={() => {
            setCurrentSegment(index)
            if (onChangeSelected) {
              onChangeSelected(index)
            }
          }}
          layoutId={layoutId}
        />
      ))}
    </Box>
  )
}
